/**
 * Reine Regeln für die Anzeige der Planungszentrale (ohne React, ohne Netz).
 * Getestet in `__tests__/planungAnzeige.test.ts`.
 */

/** Wie viele Tage die Spalte „Erledigt“ ohne „Alle anzeigen“ zurückreicht. */
export const ERLEDIGT_TAGE = 14;

const BLOCK_ENDE = /<\/(p|div|li|h[1-6]|blockquote|pre|tr)>|<br\s*\/?>/gi;

const ENTITAETEN: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  "#39": "'",
};

function entitaetenAufloesen(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+|#39);/gi, (ganz, name: string) => {
    const klein = name.toLowerCase();
    if (klein in ENTITAETEN) return ENTITAETEN[klein];
    if (klein.startsWith("#x")) {
      const n = parseInt(klein.slice(2), 16);
      return Number.isFinite(n) ? String.fromCodePoint(n) : ganz;
    }
    if (klein.startsWith("#")) {
      const n = parseInt(klein.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : ganz;
    }
    return ganz;
  });
}

/**
 * Beschreibung als Klartext für die Karte: Tags weg, Absätze und Zeilenumbrüche
 * werden zu Leerzeichen, Entitäten aufgelöst, Weißraum zusammengezogen.
 * Ohne das stand auf den Karten roh `<p>` und `<a target=...>`.
 */
export function htmlZuKlartext(html: string | null | undefined): string {
  if (!html) return "";
  const ohneSkripte = html.replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ");
  const mitLuecken = ohneSkripte.replace(BLOCK_ENDE, " ");
  const ohneTags = mitLuecken.replace(/<[^>]*>/g, "");
  return entitaetenAufloesen(ohneTags).replace(/\s+/g, " ").trim();
}

/** Enthält der Text HTML-Auszeichnung (dann wird er als HTML gerendert, sonst als Text)? */
export function istHtml(text: string | null | undefined): boolean {
  return !!text && /<\/?[a-z][\s\S]*?>/i.test(text);
}

/** Tag als `YYYY-MM-DD` in Ortszeit (nicht UTC, sonst springt der Tag abends um). */
export function tagSchluessel(datum: Date): string {
  const j = datum.getFullYear();
  const m = String(datum.getMonth() + 1).padStart(2, "0");
  const t = String(datum.getDate()).padStart(2, "0");
  return `${j}-${m}-${t}`;
}

/** `YYYY-MM-DD` als lokales Datum um Mitternacht. */
export function tagAusSchluessel(schluessel: string): Date {
  const [j, m, t] = schluessel.slice(0, 10).split("-").map(Number);
  return new Date(j, (m || 1) - 1, t || 1);
}

/** Sonntag der laufenden Woche (Woche beginnt Montag). */
export function wochenEnde(heute: Date): Date {
  const tag = heute.getDay(); // 0 = Sonntag
  const bisSonntag = tag === 0 ? 0 : 7 - tag;
  return new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() + bisSonntag);
}

export type Faelligkeit = "ueberfaellig" | "heute" | "woche" | "spaeter";

/**
 * Einordnung des Fälligkeitsdatums. Ohne Datum `null` (dann gibt es keinen Chip).
 * Erledigte Aufgaben sind nie überfällig.
 */
export function faelligkeit(
  dueDate: string | null | undefined,
  heute: Date,
  erledigt = false,
): Faelligkeit | null {
  if (!dueDate) return null;
  const heuteS = tagSchluessel(heute);
  const tag = dueDate.slice(0, 10);
  if (tag < heuteS) return erledigt ? "spaeter" : "ueberfaellig";
  if (tag === heuteS) return "heute";
  if (tag <= tagSchluessel(wochenEnde(heute))) return "woche";
  return "spaeter";
}

/** Text des Fälligkeits-Chips: „Heute“, „Morgen“, „Fr 10.10.“, „seit 06.10.“. */
export function faelligkeitText(dueDate: string, heute: Date, erledigt = false): string {
  const tag = tagAusSchluessel(dueDate);
  const heuteS = tagSchluessel(heute);
  const morgen = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() + 1);
  // „10.10“ ohne Schlusspunkt (ICU hängt ihn je nach Umgebung an oder nicht)
  const kurz = tag.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }).replace(/\.$/, "");
  const s = dueDate.slice(0, 10);
  if (s === heuteS) return "Heute";
  if (s === tagSchluessel(morgen)) return "Morgen";
  if (s < heuteS && !erledigt) return `seit ${kurz}.`;
  const wochentag = tag.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "");
  return `${wochentag} ${kurz}.`;
}

/** Initialen aus Vor- und Nachname, sonst aus dem Anzeigenamen. */
export function initialen(vorname?: string | null, nachname?: string | null, anzeige?: string): string {
  const a = (vorname ?? "").trim();
  const b = (nachname ?? "").trim();
  if (a || b) return `${a[0] ?? ""}${b[0] ?? ""}`.toUpperCase();
  const teile = (anzeige ?? "").trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return "?";
  return `${teile[0][0]}${teile.length > 1 ? teile[teile.length - 1][0] : ""}`.toUpperCase();
}

interface ErledigtZeile {
  status: string;
  updated_at: string;
}

/**
 * Erledigte Aufgaben der letzten `ERLEDIGT_TAGE` Tage (nach `updated_at`, das beim
 * Abhaken gesetzt wird). Mit `alle` ungefiltert.
 */
export function erledigtSichtbar<T extends ErledigtZeile>(aufgaben: T[], heute: Date, alle: boolean): T[] {
  const erledigt = aufgaben.filter((a) => a.status === "done");
  if (alle) return erledigt;
  const grenze = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() - ERLEDIGT_TAGE).getTime();
  return erledigt.filter((a) => new Date(a.updated_at).getTime() >= grenze);
}

/** Herkunft einer Aufgabe (Spalte `quelle`). */
export const QUELLEN: { wert: string; label: string; satz: string }[] = [
  { wert: "manuell", label: "Manuell", satz: "Von Hand angelegt" },
  { wert: "meeting", label: "Meeting", satz: "Aus einem Meeting" },
  { wert: "mail", label: "Mail", satz: "Aus einer Mail" },
  { wert: "claude", label: "Claude", satz: "Von Claude angelegt" },
];

/** „Aus einem Meeting · Meeting Daniel/Bianca 08.10.“ */
export function herkunftText(quelle: string | null | undefined, info?: string | null): string {
  const satz = QUELLEN.find((q) => q.wert === quelle)?.satz ?? "Von Hand angelegt";
  const zusatz = (info ?? "").trim();
  return zusatz ? `${satz} · ${zusatz}` : satz;
}

/**
 * Der Knopf steht nur noch im Kurseditor („Aufgaben für diesen Kurs“, die Kurs-Aufgaben laufen in die Planungszentrale).
 * Auf allen anderen Seiten fällt „Aufgaben für diese Seite“ weg (Teammeeting 20.10.2026, Daniel: „das kann hier auch weg“).
 */
export function aufgabenKnopfZeigen(pathname: string): boolean {
  return /^\/admin\/dashboard\/kurse\/[a-f0-9-]+/.test(pathname);
}

/** Satz zu D27 beim Anlegen: wer neue Aufgaben zuerst sieht („Daniel und Bianca“). */
export function vorabHinweis(namen: string[]): string {
  const wer = namen.length <= 1 ? namen.join("") : `${namen.slice(0, -1).join(", ")} und ${namen[namen.length - 1]}`;
  return (
    `Neue Aufgaben sehen zuerst nur ${wer}, dazu wer sie anlegt und wer sie bekommt. ` +
    "Für alle anderen sind sie ausgeblendet, bis jemand sie hier mit „Für alle freigeben“ öffnet."
  );
}
